import React, { useEffect, useRef, useState } from "react";

type Props = {
    isOpen: boolean;
    initialValue?: number; // 0 - 100
    onClose: () => void;
    onSave: (value: number) => void;
};

export default function CompressionModal({ isOpen, initialValue = 80, onClose, onSave }: Props) {
    const [value, setValue] = useState<number>(clampInt(initialValue));
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        // reset when opened
        if (isOpen) {
            setValue(clampInt(initialValue));
            setError(null);
            // focus the number input after a tick
            setTimeout(() => inputRef.current?.focus());
            // prevent background scroll
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen, initialValue]);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (!isOpen) return;
            if (e.key === "Escape") onClose();
            if (e.key === "Enter") handleSave();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, value]);

    function clampInt(v: number) {
        if (Number.isNaN(v)) return 0;
        return Math.min(100, Math.max(0, Math.trunc(v)));
    }

    function handleNumberChange(next: string) {
        // allow empty value while typing
        if (next.trim() === "") {
            setValue(0);
            setError(null);
            return;
        }
        const n = Number(next);
        if (Number.isNaN(n)) {
            setError("Please enter a valid integer between 0 and 100");
            return;
        }
        const clamped = clampInt(n);
        setValue(clamped);
        setError(null);
    }

    function handleSliderChange(n: number) {
        setValue(clampInt(n));
        setError(null);
    }

    function handleSave() {
        const final = clampInt(value);
        if (final < 0 || final > 100) {
            setError("Value must be between 0 and 100");
            return;
        }
        onSave(final);
        onClose();
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                aria-hidden
                onClick={onClose}
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label="Set image compression rate"
                className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5"
            >
                <header className="mb-4 flex items-start justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">Image compression rate</h3>
                        <p className="mt-1 text-sm text-slate-500">Enter an integer from 0 (no compression) to 100 (max compression).</p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="ml-4 rounded-md px-2 py-1 text-sm hover:bg-slate-100"
                    >
                        ✕
                    </button>
                </header>

                <div className="space-y-4">
                    {/* numeric input + steppers */}
                    <div className="flex items-center gap-3">
                        <div className="flex w-full items-center gap-2">
                            <button
                                onClick={() => handleSliderChange(value - 1)}
                                className="rounded-md border px-2 py-1 text-sm disabled:opacity-50"
                                disabled={value <= 0}
                                type="button"
                            >
                                −
                            </button>

                            <input
                                ref={inputRef}
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={100}
                                step={1}
                                value={value}
                                onChange={(e) => handleNumberChange(e.target.value)}
                                className="w-20 appearance-none rounded-md border px-3 py-2 text-center text-lg font-medium outline-none focus:ring-2 focus:ring-[#C62BC9]"
                                aria-label="Compression rate (0 to 100)"
                            />

                            <button
                                onClick={() => handleSliderChange(value + 1)}
                                className="rounded-md border px-2 py-1 text-sm disabled:opacity-50"
                                disabled={value >= 100}
                                type="button"
                            >
                                +
                            </button>

                            <div className="ml-3 text-sm text-slate-600">% compression</div>
                        </div>
                    </div>

                    {/* slider */}
                    <div className="mt-2">
                        <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={value}
                            onChange={(e) => handleSliderChange(Number(e.target.value))}
                            className="w-full appearance-none h-2 rounded-lg bg-slate-200      ![&::-webkit-slider-thumb]:bg-gradient-to-r from-[#FFC951] to-[#C94DAA] "
                            aria-label="Compression slider"
                        />
                        <div className="mt-2 flex justify-between text-xs text-slate-500">
                            <p className="flex items-center justify-center">0 (Highest Quality)</p>
                            <span>50</span>
                            <span>(Lowest Quality)100</span>
                        </div>
                    </div>

                    {error && <div className="text-sm text-red-600">{error}</div>}

                    <div className="mt-4 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            type="button"
                            className="rounded-md border px-4 py-2 text-sm hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            type="button"
                            className="rounded-2xl bg-gradient-to-r from-[#FFC951] to-[#C94DAA] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#861696]"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );
}

